import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CreditCard, Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import JeaInput from "@/components/jea/JeaInput";
import JeaButton from "@/components/jea/JeaButton";
import { apiClient, formatApiError } from "@/lib/apiClient";

const brandFromCard = (cn) => {
  const s = cn.replace(/\s+/g, "");
  if (s.startsWith("4")) return "Visa";
  if (/^5[1-5]/.test(s) || /^2[2-7]/.test(s)) return "Mastercard";
  if (/^3[47]/.test(s)) return "Amex";
  if (/^6/.test(s)) return "Discover";
  return "Visa";
};

/**
 * PaymentMethodModal — mocked Stripe UI. Validates only that card
 * looks well-formed and then PATCHes /billing/payment-method.
 */
const PaymentMethodModal = ({ open, onClose, onSaved }) => {
  const [cardNumber, setCardNumber] = useState("");
  const [exp, setExp] = useState("");
  const [cvc, setCvc] = useState("");
  const [zip, setZip] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const digits = cardNumber.replace(/\s+/g, "");
    if (!/^\d{15,16}$/.test(digits)) {
      toast.error("Card number must be 15 or 16 digits.");
      return;
    }
    if (!/^\d{2}\/\d{2}$/.test(exp)) {
      toast.error("Expiry must be MM/YY.");
      return;
    }
    if (!/^\d{3,4}$/.test(cvc)) {
      toast.error("CVC must be 3–4 digits.");
      return;
    }
    if (!/^\d{5}$/.test(zip)) {
      toast.error("ZIP must be 5 digits.");
      return;
    }
    const [mm, yy] = exp.split("/");
    const brand = brandFromCard(digits);
    setSaving(true);
    try {
      const { data } = await apiClient.patch("/billing/payment-method", {
        brand,
        last4: digits.slice(-4),
        exp_month: parseInt(mm, 10),
        exp_year: 2000 + parseInt(yy, 10),
      });
      toast.success(`Payment method updated (${brand} ···· ${digits.slice(-4)}).`);
      onSaved?.(data.payment_method);
      onClose();
      setCardNumber(""); setExp(""); setCvc(""); setZip("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const formatCard = (v) =>
    v.replace(/\D/g, "").slice(0, 16).replace(/(\d{4})(?=\d)/g, "$1 ");
  const formatExp = (v) => {
    const d = v.replace(/\D/g, "").slice(0, 4);
    return d.length <= 2 ? d : `${d.slice(0, 2)}/${d.slice(2)}`;
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-[12px] z-[95]"
            onClick={saving ? undefined : onClose}
          />
          <motion.div
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
            className="fixed inset-0 z-[96] flex items-center justify-center px-4 py-6"
            data-testid="payment-method-modal"
          >
            <div
              className="w-full max-w-[520px] max-h-[92vh] overflow-y-auto bg-bg-secondary border border-border-default rounded-[16px] p-8 shadow-elevated"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-5">
                <div>
                  <div className="jea-caption">Update</div>
                  <h2 className="text-white font-bold text-[20px]">Payment Method</h2>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  disabled={saving}
                  className="p-2 text-text-secondary hover:text-white"
                  data-testid="payment-method-close"
                >
                  <X size={16} />
                </button>
              </div>

              <div className="rounded-[10px] bg-bg-primary border border-border-default p-3 mb-5 flex items-start gap-2">
                <Lock size={13} className="text-text-secondary shrink-0 mt-[3px]" />
                <p className="text-text-secondary text-[12px] leading-5">
                  This is a <strong className="text-white">mocked Stripe interface</strong>.
                  No real card data is stored or charged. Only the last 4 digits and brand persist.
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="jea-caption block mb-1.5" htmlFor="pm-card">Card Number</label>
                  <JeaInput
                    id="pm-card"
                    leftIcon={<CreditCard size={14} />}
                    placeholder="4242 4242 4242 4242"
                    value={cardNumber}
                    onChange={(e) => setCardNumber(formatCard(e.target.value))}
                    inputMode="numeric"
                    data-testid="payment-method-card"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="jea-caption block mb-1.5" htmlFor="pm-exp">Expiry</label>
                    <JeaInput
                      id="pm-exp"
                      placeholder="MM/YY"
                      value={exp}
                      onChange={(e) => setExp(formatExp(e.target.value))}
                      inputMode="numeric"
                      data-testid="payment-method-exp"
                    />
                  </div>
                  <div>
                    <label className="jea-caption block mb-1.5" htmlFor="pm-cvc">CVC</label>
                    <JeaInput
                      id="pm-cvc"
                      placeholder="123"
                      value={cvc}
                      onChange={(e) => setCvc(e.target.value.replace(/\D/g, "").slice(0, 4))}
                      inputMode="numeric"
                      data-testid="payment-method-cvc"
                    />
                  </div>
                </div>
                <div>
                  <label className="jea-caption block mb-1.5" htmlFor="pm-zip">Billing ZIP</label>
                  <JeaInput
                    id="pm-zip"
                    placeholder="94103"
                    value={zip}
                    onChange={(e) => setZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
                    inputMode="numeric"
                    data-testid="payment-method-zip"
                  />
                </div>
              </div>

              <div className="mt-6 flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={saving}
                  className="h-11 px-5 rounded-[8px] border border-border-default text-text-secondary hover:text-white hover:bg-bg-tertiary text-[14px] font-semibold transition-colors disabled:opacity-40"
                  data-testid="payment-method-cancel"
                >
                  Cancel
                </button>
                <JeaButton onClick={submit} loading={saving} className="gap-2" data-testid="payment-method-submit">
                  {saving ? (<><Loader2 size={14} className="animate-spin" /> Saving…</>) : (<>Save Card</>)}
                </JeaButton>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default PaymentMethodModal;
